import { distinctUntilChanged, fromEvent, map, skip } from 'rxjs';
import { store, store$ } from '@redux/store';
import { PillsSection, getPillAction, getPillsByCounts } from 'features/popularSearches';
import { ResultType, selectResultType } from 'features/searchResultsFacets';
import { selectUrlSubdirectory } from 'features/routing';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { selectPopularSearchesPills } from '../redux/selectors';

const fetchPillAction = (data) => {
  const { pillType, isChecked } = data;
  const state = store.getState();
  const subdirectoryUrl = selectUrlSubdirectory(state);
  const fetchAction = getPillAction(subdirectoryUrl, pillType, isChecked);
  if (fetchAction) {
    store.dispatch(fetchAction(isChecked));
  }
};

const click$ = fromEvent(document.body, 'pillClickEvent').pipe(map((event) => event.detail.data));
click$.subscribe(fetchPillAction);

// Hydrate a new PillsSection on the initial server-side-rendered popular searches pills elements.
const popularSearchesPillsSelector = '[data-popular-searches-pills-group]';
const pillsSection = new PillsSection(popularSearchesPillsSelector);

/**
 * Hides the pillsSection element.
 */
const hidePillsSection = () => pillsSection.hide();


/**
 * Updates the pills section on the DOM based on the search counts and the current URL subdirectory.
 */
function onPopularSearchPillsChange({ pillsWithCounts, urlSubdirectory, popularPillFilters, locationType }) {
  const popularPillsDetails = getPillsByCounts(urlSubdirectory, pillsWithCounts, locationType);
  if (popularPillsDetails?.length) {
    pillsSection.updatePillsOnDOM(popularPillsDetails, popularPillFilters);
  } else {
    hidePillsSection();
  }
}

/**
 * Tracks the user events related to activated pills in a
 * search interface and sends the data to Google Analytics.
 */
function googleAnalyticsEventsForPills() {
  const activatedPillsText = pillsSection.getActivatedPillsText();

  if (activatedPillsText) {
    const state = store.getState();
    const resultType = selectResultType(state);
    const eventCategoty = 'Search Events';
    const eventAction = 'Popular Refinement';
    const resultTypeName = resultType === ResultType.Communities.toString() ? 'Community' : 'Homes';
    const eventLabel = `${resultTypeName} SRP - ${activatedPillsText.join(', ')}`;

    const googleAnalyticsDTO = new GoogleAnalyticsEvent(eventCategoty, eventAction, eventLabel);

    if (googleAnalyticsDTO) {
      store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));
    }
  }
}

/**
 * Create an observable pipeline to subscribe the `click$`. When a click
 * event occurs, the function is executed to log the GA Event for the Popular Searches Pills.
 */
click$.subscribe(googleAnalyticsEventsForPills);

/**
 * The code snippet is creating an observable pipeline using RxJS operators to subscribe to changes in
 * the Redux store.
 */
store$
  .pipe(
    map(selectPopularSearchesPills),
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
    skip(1)
  )
  .subscribe(onPopularSearchPillsChange);
